import type { MaybeComputedElementRef } from '@vueuse/core';
import { unrefElement, useEventListener } from '@vueuse/core';
import { computed } from 'vue';

interface ITrackedTouch {
    identifier: number;
    clientY: number;
}

function findTouchByIdentifier(touchList: TouchList, identifier: number): Touch | null {
    return Array.from(touchList).find(touch => touch.identifier === identifier) ?? null;
}

export function useBindExternalScrollArea(
    externalScrollArea: MaybeComputedElementRef,
    scrollArea: MaybeComputedElementRef,
) {
    const externalScrollAreaElement = computed(() => unrefElement(externalScrollArea));
    const scrollAreaElement = computed(() => unrefElement(scrollArea));
    let trackedTouch: ITrackedTouch | null = null;

    function isInsideScrollArea(target: EventTarget | null): boolean {
        return target instanceof Node && (scrollAreaElement.value?.contains(target) ?? false);
    }

    function onTouchStart(event: TouchEvent) {
        const touch = event.changedTouches[0];
        const isAnotherTouchActive = trackedTouch !== null || event.touches.length > 1;
        if (touch === undefined || isAnotherTouchActive || isInsideScrollArea(event.target)) {
            return;
        }
        trackedTouch = {
            identifier: touch.identifier,
            clientY: touch.clientY,
        };
    }

    function onTouchMove(event: TouchEvent) {
        if (trackedTouch === null || scrollAreaElement.value === undefined || scrollAreaElement.value === null) {
            return;
        }
        const touch = findTouchByIdentifier(event.touches, trackedTouch.identifier);
        if (touch === null) {
            return;
        }
        if (event.cancelable) {
            event.preventDefault();
        }
        scrollAreaElement.value.scrollTop += trackedTouch.clientY - touch.clientY;
        trackedTouch.clientY = touch.clientY;
    }

    function onTouchEnd(event: TouchEvent) {
        if (trackedTouch === null || findTouchByIdentifier(event.changedTouches, trackedTouch.identifier) === null) {
            return;
        }
        trackedTouch = null;
    }

    useEventListener(externalScrollAreaElement, 'touchstart', onTouchStart, { passive: true });
    useEventListener(externalScrollAreaElement, 'touchmove', onTouchMove, { passive: false });
    useEventListener(externalScrollAreaElement, 'touchend', onTouchEnd, { passive: true });
    useEventListener(externalScrollAreaElement, 'touchcancel', onTouchEnd, { passive: true });
}
